import type { LegalDoc } from '@/data/legal/types';

export const customerTerms: LegalDoc = {
  title: 'Customer Terms & Conditions',
  lastUpdated: '15 October 2026',
  sections: [
    {
      heading: '1. About KOR38',
      blocks: [
        {
          p: 'KOR38 is operated by Daniella Farrell, trading as KOR38, an independent business based in London, United Kingdom.',
        },
        {
          p: 'KOR38 provides personalised event-supplier sourcing and introduction services, primarily across Greater London. Enquiries for other locations may be considered.',
        },
        {
          p: 'These Terms & Conditions explain what customers can expect when using our services, how our fees work and the responsibilities of everyone involved.',
        },
        { p: 'Contact: hello@kor38.com' },
      ],
    },
    {
      heading: '2. What KOR38 Does',
      blocks: [
        {
          p: 'KOR38 helps customers identify and connect with potentially suitable suppliers for weddings, private celebrations, corporate events and other occasions.',
        },
        {
          p: 'Our role is to understand your requirements, source suitable supplier options where applicable, and facilitate introductions.',
        },
        {
          p: 'KOR38 is not an event planning or event management company, booking platform or supplier directory.',
        },
        { p: 'Unless separately agreed in writing, we do not:' },
        {
          list: [
            'Plan, organise or coordinate your event.',
            'Book suppliers on your behalf.',
            'Negotiate or enter into supplier contracts for you.',
            'Collect payments on behalf of suppliers.',
            'Supervise suppliers or manage their work.',
            'Guarantee supplier availability, performance or suitability.',
          ],
        },
        {
          p: 'Customers remain responsible for selecting and booking suppliers and managing their event.',
        },
      ],
    },
    { heading: '3. Our Services', blocks: [] },
    {
      heading: '3.1 Personal Match — Free',
      blocks: [
        {
          p: 'Personal Match is a complimentary introduction service for customers looking for a suitable supplier or specialist.',
        },
        {
          p: 'Where Daniella knows of a potentially suitable supplier within her existing network, she may offer an introduction.',
        },
        {
          p: 'This service does not include extensive supplier research or a commitment to find a supplier. There is no charge to the customer for Personal Match, and no guarantee that an introduction can be made.',
        },
      ],
    },
    {
      heading: '3.2 48-Hour Event Rescue — £79',
      blocks: [
        {
          p: '48-Hour Event Rescue is designed for customers who urgently need help finding a supplier or have a particularly difficult supplier requirement.',
        },
        { p: 'The £79 service covers one agreed supplier requirement.' },
        {
          p: 'Before accepting payment, Daniella will review the brief and confirm whether KOR38 can reasonably undertake the request.',
        },
        {
          p: 'If accepted, KOR38 will aim to provide suitable supplier options or a meaningful progress update within 48 actual elapsed hours of the agreed start time.',
        },
        { p: 'The agreed start time will be confirmed with the customer in writing.' },
        {
          p: 'The 48-hour period does not begin when an enquiry form is submitted. It begins only after the brief has been accepted, the required payment has been made and the agreed start time has arrived.',
        },
        {
          p: 'The service is a commitment to undertake focused sourcing work within the agreed timeframe. It is not a guarantee that a suitable supplier will be available, agree to accept the booking or meet every requirement.',
        },
        { p: 'Any additional supplier requirements will need to be considered separately.' },
      ],
    },
    {
      heading: '3.3 Event Dream Team — From £199',
      blocks: [
        {
          p: 'Event Dream Team is a personalised sourcing service for customers requiring suppliers across multiple categories.',
        },
        {
          p: 'The service may include reviewing your event requirements, identifying potentially suitable suppliers and facilitating introductions across the agreed categories.',
        },
        { p: 'Prices start at £199 for suitable, relatively straightforward briefs.' },
        {
          p: 'The final fee depends on the number of supplier categories, complexity, timescales and research required.',
        },
        {
          p: 'Daniella will review your requirements and provide a personalised quotation before any payment is made.',
        },
        { p: 'The quotation will confirm the agreed scope of work, fee and anticipated delivery timeframe.' },
        {
          p: 'KOR38 does not become responsible for coordinating or managing the suppliers introduced through this service.',
        },
      ],
    },
    {
      heading: '4. Submitting an Enquiry',
      blocks: [
        {
          p: 'Submitting an enquiry or service request does not automatically create a paid contract with KOR38.',
        },
        {
          p: 'Daniella may contact you to clarify your requirements, discuss availability or request additional information. KOR38 reserves the right to decline a request where it is outside our services, resources or reasonable ability to deliver.',
        },
        {
          p: 'No payment is required unless and until a paid brief has been accepted and the customer has agreed to the relevant service fee.',
        },
      ],
    },
    {
      heading: '5. Acceptance, Payment and Starting Work',
      blocks: [
        { p: 'For paid services, KOR38 will confirm in writing:' },
        {
          list: [
            'The service being provided.',
            'The agreed scope of work.',
            'The total service fee.',
            'Any applicable delivery timeframe.',
            'When the service is expected to begin.',
            'Any information required from the customer.',
            'Applicable cancellation terms.',
          ],
        },
        {
          p: 'A paid service contract is formed when KOR38 accepts the brief and the customer accepts the confirmed quotation.',
        },
        {
          p: 'Payment arrangements will be communicated before work begins. Unless otherwise agreed in writing, payment is required before the service commences.',
        },
        {
          p: 'Any additional work outside the agreed scope will be discussed and priced separately. No additional charges will be made without the customer\u2019s agreement.',
        },
      ],
    },
    {
      heading: '6. Customer Responsibilities',
      blocks: [
        { p: 'To help KOR38 provide an effective service, customers agree to:' },
        {
          list: [
            'Provide accurate and reasonably complete information.',
            'Explain their requirements, budget, location and relevant deadlines.',
            'Respond to reasonable requests for clarification.',
            'Inform KOR38 promptly of significant changes.',
            'Check supplier suitability, availability, quotations and contractual terms before making a booking.',
          ],
        },
        {
          p: 'Customers should independently verify any licences, insurance, qualifications or other requirements relevant to the supplier\u2019s services.',
        },
      ],
    },
    {
      heading: '7. Supplier Introductions and Independence',
      blocks: [
        { p: 'Suppliers introduced by KOR38 are independent businesses or professionals.' },
        {
          p: 'Customers deal directly with their chosen suppliers when requesting quotations, confirming availability, making bookings, signing contracts and arranging payments.',
        },
        { p: 'Any contract for supplier services is between the customer and the supplier.' },
        {
          p: 'Suppliers are responsible for their own quotations, contractual commitments, insurance, licences, staffing, service delivery and applicable legal requirements.',
        },
        {
          p: 'KOR38 will use reasonable care and skill when carrying out its own sourcing and introduction services but does not guarantee the actions or performance of independent suppliers.',
        },
        {
          p: 'If a problem arises with a supplier\u2019s booking or services, the customer should contact that supplier directly. KOR38 may assist with communication where reasonably possible but is not obliged to manage or resolve supplier disputes.',
        },
      ],
    },
    {
      heading: '8. Referral Commissions',
      blocks: [
        {
          p: 'KOR38 may receive referral fees or commissions from suppliers where an introduction results in a confirmed booking.',
        },
        {
          p: 'Any such arrangements are agreed with the relevant supplier in advance and disclosed transparently where applicable.',
        },
        { p: 'Supplier referral commissions are separate from any service fee paid by the customer.' },
        {
          p: 'Customers will not be charged additional KOR38 referral fees without their prior agreement.',
        },
        {
          p: 'A supplier\u2019s willingness to pay a referral commission does not guarantee an introduction or booking.',
        },
      ],
    },
    {
      heading: '9. Availability and Outcomes',
      blocks: [
        { p: 'KOR38 will use reasonable care and skill when delivering an accepted service.' },
        {
          p: 'However, supplier availability, pricing, willingness to accept work and final booking decisions are outside KOR38\u2019s direct control.',
        },
        { p: 'We cannot guarantee:' },
        {
          list: [
            'That every supplier requirement can be fulfilled.',
            'That a particular supplier will be available.',
            'That quotations will fall within the customer\u2019s budget.',
            'That a supplier will accept a booking.',
            'That an event will achieve a particular outcome.',
          ],
        },
        {
          p: 'These limitations do not remove KOR38\u2019s responsibility to deliver its own agreed services with reasonable care and skill.',
        },
      ],
    },
    { heading: '10. Cancellation and Refunds', blocks: [] },
    {
      heading: '10.1 Your statutory cancellation rights',
      blocks: [
        {
          p: 'Where a consumer purchases a service online, by telephone or through another qualifying distance-selling arrangement, they will generally have a 14-day statutory cancellation period beginning the day after the contract is entered into.',
        },
        {
          p: 'There are circumstances in which a customer may request that a service begins during this period.',
        },
      ],
    },
    {
      heading: '10.2 Starting work during the cancellation period',
      blocks: [
        {
          p: 'If you want KOR38 to begin a paid service before the statutory cancellation period has expired, we will ask you to make an express request.',
        },
        { p: 'We will also explain that:' },
        {
          list: [
            'If you cancel after work has started, you may be required to pay a proportionate amount for services already provided, where permitted by law.',
            'If the service is fully performed during the cancellation period, you may lose the statutory right to cancel, provided you expressly requested early performance and acknowledged this consequence.',
          ],
        },
        { p: 'These arrangements will be confirmed before work begins.' },
      ],
    },
    {
      heading: '10.3 Cancelling before work begins',
      blocks: [
        {
          p: 'If you exercise an applicable statutory right to cancel before work begins, you will receive a full refund.',
        },
      ],
    },
    {
      heading: '10.4 Cancelling after work begins',
      blocks: [
        {
          p: 'Where work has started following a valid request for early performance, any refund will be calculated in accordance with applicable consumer law and the work already carried out.',
        },
        { p: 'We will explain the calculation and confirm any refund due.' },
      ],
    },
    {
      heading: '10.5 Where KOR38 cannot provide the agreed service',
      blocks: [
        {
          p: 'If KOR38 cannot undertake an accepted service, we will discuss the circumstances and provide an appropriate refund or other resolution.',
        },
        {
          p: 'If KOR38 fails to provide the agreed service with reasonable care and skill or does not meet its contractual obligations, customers retain their statutory rights, including applicable rights to repeat performance or a price reduction.',
        },
      ],
    },
    {
      heading: '10.6 How to cancel',
      blocks: [
        { p: 'To request cancellation, email: hello@kor38.com' },
        {
          p: 'Include your name, the service purchased and a clear statement that you wish to cancel.',
        },
        { p: 'You may also use the statutory model cancellation form where applicable.' },
        {
          p: 'Any refund due will be processed within the period required by law, normally using the original payment method unless otherwise agreed.',
        },
      ],
    },
    {
      heading: '11. Changes to an Agreed Brief',
      blocks: [
        {
          p: 'If you need to change your event date, location, budget or supplier requirements after a service has been accepted, please contact KOR38 as soon as possible.',
        },
        { p: 'We will assess whether the changes can reasonably be accommodated.' },
        {
          p: 'Significant changes may require a revised scope, timeframe or fee. No additional fees will be charged without your agreement.',
        },
        {
          p: 'If the original service can no longer be provided as agreed, we will discuss the available options and any applicable cancellation or refund arrangements.',
        },
      ],
    },
    {
      heading: '12. Complaints and Concerns',
      blocks: [
        {
          p: 'If you are dissatisfied with a KOR38 service, please contact: hello@kor38.com',
        },
        { p: 'Explain the issue and provide any relevant details.' },
        { p: 'We will review your concerns and aim to respond within a reasonable time.' },
        {
          p: 'Where something has gone wrong with our service, we will seek an appropriate resolution in accordance with our contractual obligations and applicable consumer law.',
        },
      ],
    },
    {
      heading: '13. Liability',
      blocks: [
        { p: 'KOR38 is responsible for providing its own agreed services with reasonable care and skill.' },
        {
          p: 'We are not automatically responsible for independent suppliers\u2019 acts, omissions, cancellations, contractual breaches or service failures.',
        },
        {
          p: 'Nothing in these terms excludes or restricts liability where doing so would be unlawful, including liability for death or personal injury caused by negligence, fraud or fraudulent misrepresentation, or customers\u2019 statutory consumer rights.',
        },
        {
          p: 'Any other limitations of liability apply only to the extent permitted by law and must be fair and reasonable.',
        },
      ],
    },
    {
      heading: '14. Personal Information',
      blocks: [
        { p: 'We collect and use personal information in accordance with our Privacy Policy.' },
        {
          p: 'Information may be shared with potentially suitable suppliers where reasonably necessary to facilitate an introduction requested by the customer.',
        },
        {
          p: 'Please refer to our Privacy Policy for further information about how personal information is handled.',
        },
      ],
    },
    {
      heading: '15. Events Outside Our Control',
      blocks: [
        {
          p: 'Occasionally, circumstances outside our reasonable control may affect our ability to deliver a service as agreed.',
        },
        {
          p: 'If this happens, we will inform affected customers as soon as reasonably possible and discuss suitable next steps.',
        },
        {
          p: 'Where the agreed service cannot be provided, customers retain any applicable contractual and statutory rights.',
        },
      ],
    },
    {
      heading: '16. Changes to These Terms',
      blocks: [
        { p: 'We may update these Terms & Conditions from time to time.' },
        {
          p: 'The version applicable to a paid service will normally be the version provided or made available when the customer enters into the contract, unless a change is subsequently agreed or required by law.',
        },
      ],
    },
    {
      heading: '17. Governing Law',
      blocks: [
        {
          p: 'These Terms & Conditions are governed by the laws of England and Wales, subject to any mandatory consumer protections and jurisdiction rights that apply.',
        },
      ],
    },
    {
      heading: '18. Contact',
      blocks: [
        { p: 'Daniella Farrell, trading as KOR38' },
        { p: 'London, United Kingdom' },
        { p: 'Email: hello@kor38.com' },
        {
          p: 'For questions about these terms, service enquiries or cancellations, please contact us using the email address above.',
        },
      ],
    },
  ],
};